// TokenPicker.tsx · v12.10.27 (#2146 Q31 {conversation.channel} in the Call group) · 2026-10-31
// TokenPicker.tsx · v12.10.12 (#1519 Call group: {call.timeGreeting} in the default catalog) · 2026-10-10
// TokenPicker.tsx · v12.10.11.1 (#1481 lang passed to the editor; resolver tokens are known) · 2026-10-09
// TokenPicker.tsx · v12.10.11 (#1458 new text uses {organization.name} - the vocabulary token; {organization.practiceName} / {tenant.practiceName} stay readable as aliases) · 2026-10-08
// v12.4.12 (#482): chipTest prop passed through to ChipEditor (JSON request bodies).
// v12.2.43.1 (#236/#246): aliasOf prop + alias/label-aware inline autocomplete (typed prefix -> canonical entity)
// TokenPicker.tsx — v12.2.38.0 (#204 Space accepts + no double-brace when "{" already typed, #205 list kept inside the field; #199 caret-anchored list)
// Shared token-insertion control: a textarea with a grouped chip panel, drag-and-drop, and
// inline `prefix.` autocomplete. Used by Voicemail, Handoff (busy message + whisper), and later
// Quick Answers. Tokens insert wrapped in braces with exactly one surrounding space.
// Spec: SPEC_shared__token_picker.md.
import { useMemo, useRef, useState } from 'react';
// v12.3.10.1 (#302): the field renders tokens as CHIPS showing the tenant's word, while the value it
// stores stays the canonical token. Caret positions come back in VALUE SPACE, so every piece of the
// autocomplete below works exactly as it did against the old <textarea>.
import { ChipEditor, type ChipEditorHandle } from './ChipEditor';
import { RESOLVER_KNOWN_TOKENS } from '../../lib/tokenPreview';   // v12.10.11.1 (#1481)

export interface TokenGroup {
  label: string;
  tokens: string[];
}

// Seed catalog (shape, not a frozen list — caller identifiers / appointment / integration tokens
// will be sourced dynamically later). Tokens are dot-prefixed; inserted as {group.token}.
// v12.4.5 (#436/#437): THIS IS THE LIST EVERY UNWIRED EDITOR GETS, SO IT MUST BE THE CANONICAL VOCABULARY.
//
// It was pre-BDR-001: an "Appointment" group of node outputs from an older model (PickSlot.*, GetSlots.*,
// BookSlot.*), an "Integrations" group of the same, and NO subject.* or request.* at all - so no
// {request.referenceNumber}, no {subject.name}. Three tenant-editable messages the ENGINE speaks fall back to
// it, because they use TokenPicker directly rather than through PromptEditor and pass no `groups`:
// the Voicemail greeting, the Handoff busy message (both via LanguageMessageEditor) and the Handoff whisper.
// Tushar's standing complaint - "i kept on finding and pointing that token is coming here and token is not
// coming there" - is exactly this: the designer's twelve editors were wired canonically by #405 and these
// three were never in that fix's scope.
//
// Fixed at the DEFAULT rather than at the three call sites, so an editor that forgets `groups` in future gets
// the right list instead of a wrong one. Check-PromptEditors.mjs now also guards <TokenPicker> so a new
// unwired one fails the build.
//
// Every token here has a working preview sample (see lib/tokenPreview.ts) - a token offered in the picker that
// previews as a [bracketed marker] is the same defect in a new coat. Node-output tokens (PickSlot.*, integration
// results) are deliberately NOT here: they depend on the workflow, and the designer passes its own catalog.
export const TOKEN_GROUPS: TokenGroup[] = [
  { label: 'Organization', tokens: ['organization.name', 'organization.phone', 'organization.address', 'organization.email', 'organization.website', 'organization.timezone'] },
  { label: 'Caller', tokens: ['caller.name', 'caller.firstName', 'caller.lastName', 'caller.phone'] },
  { label: 'Patient', tokens: ['subject.name', 'subject.firstName', 'subject.lastName', 'subject.dob', 'subject.phone', 'subject.zip', 'subject.memberId'] },
  { label: 'Appointment', tokens: ['request.date', 'request.time', 'request.newDate', 'request.newTime', 'request.reason', 'request.referenceNumber'] },
  // v12.10.12 (#1519 T66): the time-of-day greeting - in every Insert token picker, this default one included.
  { label: 'Call', tokens: ['call.timeGreeting', 'conversation.channel'] },   // v12.10.27 (#2146 Q31): "call" or "text"
];


// Insert `token` (raw, no braces) at `pos`, wrapping in {} and ensuring exactly one surrounding
// space when adjacent to non-whitespace (no double space, no leading/trailing space at edges).
function spacedInsert(text: string, pos: number, token: string): { next: string; caret: number } {
  const before = text.slice(0, pos);
  const after = text.slice(pos);
  let insert = `{${token}}`;
  if (before.length > 0 && !/\s$/.test(before)) insert = ' ' + insert;
  if (after.length > 0 && !/^\s/.test(after)) insert = insert + ' ';
  const next = before + insert + after;
  return { next, caret: before.length + insert.length };
}

interface AutocompleteState { open: boolean; partial: string; start: number; items: string[]; sel: number; top: number; left: number; }
const CLOSED: AutocompleteState = { open: false, partial: '', start: 0, items: [], sel: 0, top: 8, left: 10 };

// v12.2.36.0 (#199)'s hidden mirror-div caret measurement was removed in v12.3.10.1 (#302): a
// contentEditable has real DOM Ranges, so ChipEditor.caretRect() measures the caret directly.

export function TokenPicker(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  onPreview?: () => void;
  groups?: TokenGroup[];   // v12.2.35.0 (#191): caller-supplied token catalog (workflow uses captured slots)
  labelOf?: (token: string) => string | undefined;   // v12.2.43.0 (#236): friendly Vocabulary label to DISPLAY (canonical token still inserted)
  aliasOf?: (typedEntity: string) => string | undefined;   // v12.2.43.1 (#236/#246): map a typed prefix (patient / visitor) to its canonical entity (subject)
  // v12.8.0 (#660): hide the Insert-token button. Used by the escalation ladder's rung sentences, where
  // Tushar ruled tokens out - "i don't thing we should even allow to insert token here for now". The
  // textarea, the preview and Play all stay; only the button goes, so the same editor still serves.
  noTokens?: boolean;
  // v12.3.10 (#300): controls rendered at the RIGHT of the Insert-token row (Auto-translate / Reset).
  // They used to sit on their own line below the field, which read as unrelated to the editor.
  rightSlot?: React.ReactNode;
  // v12.4.12 (#482): passed to ChipEditor - which {…} render as chips. Integrations bodies are JSON.
  chipTest?: (inner: string) => boolean;
  /** v12.10.11.1 (#1481): the text's language - set as lang on the editable element (spell-check in that language). */
  lang?: string;
}) {
  const edRef = useRef<ChipEditorHandle>(null);
  const caretRef = useRef<number>(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [ac, setAc] = useState<AutocompleteState>(CLOSED);
  const groups = useMemo(() => props.groups ?? TOKEN_GROUPS, [props.groups]);
  const allTokens = useMemo(() => groups.flatMap((g) => g.tokens), [groups]);
  const prefixes = useMemo(() => Array.from(new Set(allTokens.map((t) => t.split('.')[0].toLowerCase()))), [allTokens]);

  const rememberCaret = () => { caretRef.current = edRef.current?.getCaret() ?? props.value.length; };

  function applyInsert(token: string, at: number) {
    const { next, caret } = spacedInsert(props.value, at, token);
    props.onChange(next);
    requestAnimationFrame(() => {
      const ed = edRef.current;
      if (ed) { ed.focus(); ed.setCaret(caret); caretRef.current = caret; }
    });
  }

  function insertChip(token: string) {
    applyInsert(token, caretRef.current);
    setPanelOpen(false);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const token = e.dataTransfer.getData('text/plain');
    if (!token) return;
    // Best-effort: use the textarea's current caret (set by the browser on drop), else remembered.
    const at = edRef.current?.getCaret() ?? caretRef.current;
    applyInsert(token, at);
  }

  function onKeyUp(e: React.KeyboardEvent<HTMLDivElement>) {
    // v12.2.36.0 (#199): don't rebuild the list on the keys that DRIVE it — otherwise keyUp resets the
    // highlighted item to 0 right after keyDown moved it, so Arrow/Enter/Tab never appear to work.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') return;
    rememberCaret();
    const ed = edRef.current;
    if (!ed) return;
    const pos = ed.getCaret();
    const upto = props.value.slice(0, pos);
    const m = upto.match(/([A-Za-z]+)\.([A-Za-z0-9_]*)$/);
    if (m) {
      // v12.2.43.1 (#236/#246): accept the typed prefix directly OR via alias/label (patient. / visitor. -> subject.)
      const typedEnt = m[1].toLowerCase();
      const canonEnt = prefixes.includes(typedEnt) ? typedEnt : props.aliasOf?.(typedEnt);
      if (canonEnt) {
        const typedFld = m[2].toLowerCase();
        const items = allTokens.filter((t) => {
          const d = t.indexOf('.'); if (d < 0) return false;
          return t.slice(0, d).toLowerCase() === canonEnt && t.slice(d + 1).toLowerCase().startsWith(typedFld);
        });
        if (items.length) {
          const caret = ed.caretRect();
          // keep the current selection if the same partial is still being edited; else start at 0
          setAc((prev) => ({ open: true, partial: m[0], start: pos - m[0].length, items,
            sel: prev.open && prev.partial === m[0] ? Math.min(prev.sel, items.length - 1) : 0,
            top: caret.top, left: caret.left }));
          return;
        }
      }
    }
    if (ac.open) setAc(CLOSED);
  }

  function acceptAc(token: string) {
    const before = props.value.slice(0, ac.start);
    const after = props.value.slice(ac.start + ac.partial.length);
    // v12.2.38.0 (#204): don't double up braces if the caller already typed "{" (or a "}" already follows).
    const hasOpen = before.endsWith('{');
    const hasClose = after.startsWith('}');
    const insert = `${hasOpen ? '' : '{'}${token}${hasClose ? '' : '}'}`;
    const merged = before + insert + after;
    props.onChange(merged);
    const caret = (before + insert).length + (hasClose ? 1 : 0);
    setAc(CLOSED);
    requestAnimationFrame(() => {
      const ed = edRef.current;
      if (ed) { ed.focus(); ed.setCaret(caret); caretRef.current = caret; }
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!ac.open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAc((s) => ({ ...s, sel: (s.sel + 1) % s.items.length })); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAc((s) => ({ ...s, sel: (s.sel - 1 + s.items.length) % s.items.length })); }
    else if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') { e.preventDefault(); acceptAc(ac.items[ac.sel]); }   // v12.2.38.0 (#204): Space accepts too
    else if (e.key === 'Escape') { setAc(CLOSED); }
  }

  const rows = props.rows ?? 4;

  return (
    <div className="tok-field">
      <div className="tok-ta-wrap">
        <ChipEditor
          ref={edRef}
          value={props.value}
          onChange={props.onChange}
          rows={rows}
          placeholder={props.placeholder}
          disabled={props.disabled}
          labelOf={props.labelOf}
          isKnown={(t) => allTokens.some((x) => x.toLowerCase() === t.toLowerCase())
            || RESOLVER_KNOWN_TOKENS.some((x) => x.toLowerCase() === t.toLowerCase())}   // v12.10.11.1 (#1481): organization.* / tenant.* the preview fills are not flagged
          chipTest={props.chipTest}
          lang={props.lang}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onCaretMove={rememberCaret}
          onDrop={onDrop}
        />
        {ac.open && (
          <div className="tok-ac" role="listbox" style={{ top: ac.top, left: ac.left, maxWidth: 'calc(100% - 8px)' }}>
            {ac.items.map((t, i) => (
              <button key={t} type="button" role="option" aria-selected={i === ac.sel}
                className={`tok-ac-item${i === ac.sel ? ' on' : ''}`}
                title={`{${t}}`}
                onMouseDown={(e) => { e.preventDefault(); acceptAc(t); }}>{props.labelOf?.(t) ? `${props.labelOf(t)}  ·  {${t}}` : `{${t}}`}</button>
            ))}
          </div>
        )}
      </div>

      <div className="lt-foot">
        {props.onPreview && <button type="button" className="lt-prev" onClick={props.onPreview} disabled={props.disabled}>Preview</button>}
        {!props.noTokens && <button type="button" className="lt-tok" onClick={() => setPanelOpen((v) => !v)} disabled={props.disabled}>Insert token</button>}
        {props.rightSlot && <span className="lt-foot-right">{props.rightSlot}</span>}
      </div>

      {panelOpen && (
        <div className="tok-panel open">
          {groups.map((g) => (
            <div key={g.label} className="tok-group">
              <span className="tok-group-lbl">{g.label}</span>
              <div className="tok-chips">
                {g.tokens.map((t) => (
                  <button key={t} type="button" className="tok-chip" draggable title={`{${t}}`}
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', t)}
                    onClick={() => insertChip(t)}>{props.labelOf?.(t) ?? `{${t}}`}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
